import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { VELLARINGATTU_MEMBER_PHRASE } from "@/data/siteStats";

/**
 * India Privacy Policy — the page linked from the DPDP consent tickbox
 * on the login form. This is a plain-language explainer of what data we
 * process for Indian users, why, and how they can withdraw consent /
 * appoint a nominee / request erasure per the Digital Personal Data
 * Protection Act, 2023.
 *
 * Owners: keep this in sync with any updates to the Terms of Use and
 * the general /legal/privacy page. Only the India-specific rights &
 * remedies live here.
 */
export default function IndiaPrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="india-privacy-page">
      <Helmet>
        <title>{`India Privacy Policy — Digital Personal Data Protection Act, 2023`}</title>
        <meta name="description" content="How Social Pulse and IndianFamilyTree.com handle personal data of Indian users under DPDP 2023." />
      </Helmet>

      <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B58936" }}>
        India · Digital Personal Data Protection Act, 2023
      </div>
      <h1 className="font-display text-4xl leading-tight" style={{ color: "#3B2E1F" }}>
        Your data. Your family archive. Your say.
      </h1>
      <p className="mt-4 text-sm" style={{ color: "#6B5A3F" }}>
        This page explains — in plain English — how we handle personal information for users we detect as being from India. For everyone else, the general <Link to="/legal/privacy" className="underline">Privacy Policy</Link> and <Link to="/legal/terms" className="underline">Terms</Link> apply.
      </p>

      <section className="mt-8 space-y-6 text-sm leading-relaxed" style={{ color: "#3B2E1F" }}>
        <div>
          <h2 className="font-display text-2xl">What we collect</h2>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Your name, phone number, and email (if provided)</li>
            <li>Family records you create — ancestors, memorials, photos, stories</li>
            <li>Sign-in metadata — provider used, IP-inferred country, device fingerprint</li>
            <li>Voluntary contributions — comments, obituaries, tree edits</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl">Why we process it</h2>
          <p className="mt-2">
            To provide you a family-archive service — public trees, memorials, obituaries, and the discovery tools that surround them. We also use aggregated, non-personal data to improve the service and to publish site-level statistics (e.g., "{VELLARINGATTU_MEMBER_PHRASE} across our public trees").
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl">Your rights under DPDP 2023</h2>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Withdraw consent</strong> — anytime from your account settings; your account and personal data will be deleted or anonymised within 30 days.</li>
            <li><strong>Appoint a nominee</strong> — designate a family member to inherit your account and archive when you pass away. Contact support to set this up.</li>
            <li><strong>Data portability</strong> — request an export of everything we hold on you in JSON format.</li>
            <li><strong>Correction & erasure</strong> — fix mistakes in your profile or delete specific records at any time.</li>
            <li><strong>Breach notification</strong> — we'll email/SMS you within 72 hours if your data is ever compromised.</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl">Cross-border transfer</h2>
          <p className="mt-2">
            Our databases live outside India. By ticking the DPDP consent checkbox, you agree that your data may be transferred to and stored on servers in the US and Europe under standard contractual clauses. If you want India-only storage, contact us to opt-in to our India-region tier (available on request).
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl">Contact</h2>
          <p className="mt-2">
            For any privacy-related question, request, or complaint under DPDP 2023, write to us at <a href="mailto:privacy@socialpulse.com" className="underline">privacy@socialpulse.com</a>. We aim to respond within 7 working days.
          </p>
        </div>

        <div className="pt-6 text-xs" style={{ color: "#6B5A3F" }}>
          Last updated: February 2026. Owners will update this notice whenever the underlying practices change.
        </div>
      </section>
    </div>
  );
}
